import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, Check, X, Edit, PlusCircle } from "lucide-react";
import { useEffect, useState } from "react";
import axiosInstance from "@/utils/axiosInstance";
import { Orders } from "@/interface/entityInterface";
import { useGetList } from "react-admin";

const OrdersTimeline = ({orderClick}: {orderClick: (order: Orders) => void}) => {
    const {data: activities = []} = useGetList("taxi-orders/history/timeline")
    const [users, setUsers] = useState<any[]>([]);

    const fetchUsers = async () => {
        try {
            const response = await axiosInstance.get(`/users`);
            setUsers(response.data);
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const getStatusContent: any = (status: any, userId: any, users: any[]) => {

        const matchedUser = users.find((u) => u.id === userId);
        const fullName = matchedUser?.firstname && matchedUser?.lastname
            ? `${matchedUser.firstname} ${matchedUser.lastname}`
            : "Website User";

        switch (status) {
            case "NEW_ORDER":
                return {
                    icon: <PlusCircle className="h-4 w-4 text-blue-500" />,
                    text: `${fullName} has placed a new taxi order. The request is now pending review.`,
                    badgeVariant: "outline",
                };
            case "ACCEPTED":
                return {
                    icon: <Check className="h-4 w-4 text-green-500" />,
                    text: `${fullName} has reviewed and accepted the order. It will be processed shortly.`,
                    badgeVariant: "outline",
                };
            case "REJECTED":
                return {
                    icon: <X className="h-4 w-4 text-red-500" />,
                    text: `${fullName} has reviewed and rejected the order. No further action will be taken.`,
                    badgeVariant: "outline",
                };
            case "UPDATED":
                return {
                    icon: <Edit className="h-4 w-4 text-yellow-500" />,
                    text: `${fullName} has made changes to the order details. Please review the updated information.`,
                    badgeVariant: "outline",
                };
            default:
                return {
                    icon: <Clock className="h-4 w-4 text-gray-500" />,
                    text: `${fullName} performed an update or action on this order.`,
                    badgeVariant: "outline",
                };
        }
    };

    const TimelineSkeleton = () => {
        return (
            <div className="space-y-6 relative border-l border-gray-200 pl-6">
                {[...Array(4)].map((_, i) => (
                    <div key={i} className="relative">
                        <div className="absolute -left-3 top-1">
                            <div className="bg-white rounded-full border shadow p-1">
                                <Skeleton className="h-4 w-4 rounded-full" />
                            </div>
                        </div>
                        <div className="ml-6 space-y-2">
                            <Skeleton className="h-4 w-2/3" />
                            <Skeleton className="h-3 w-full" />
                            <div className="flex gap-2">
                                <Skeleton className="h-4 w-20 rounded-full" />
                                <Skeleton className="h-4 w-24" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const handleOrderClick = async (id: number) => {
        const {data} = await axiosInstance.get<Orders>(`/taxi-orders/${id}`)

        orderClick(data)
    } 

    return (
        <ScrollArea className="h-auto w-full rounded-md border p-4 dark:bg-dark shadow-sm">
            <h2 className="text-lg font-semibold mb-4">Orders Timeline</h2>
            {activities.length === 0 ? (
                <TimelineSkeleton />
            ) : (
                <div className="space-y-6 relative border-l border-gray-200 pl-6">
                    {activities?.length > 0 ? (
                        activities.map((event: any) => {
                            const { icon, text, badgeVariant } = getStatusContent(event.status, event.user_id, users);

                            return (
                                <div key={event.id} className="relative pb-2">
                                    <div className="absolute -left-3 top-1">
                                        <div className="bg-white rounded-full border shadow p-1">
                                            {icon}
                                        </div>
                                    </div>
                                    <div className="ml-6">
                                        <p className="text-sm">{text}</p>
                                        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                                            {event.data.id && (
                                                <Badge 
                                                    variant={badgeVariant} 
                                                    className="cursor-pointer"
                                                    onClick={() => handleOrderClick(event.data.id)}
                                                >#  {event.data.id}</Badge>
                                            )}
                                            {event.status && (
                                                <Badge variant={badgeVariant}>{event.status}</Badge>
                                            )}
                                            <span>
                                                {new Date(event?.created_at).toLocaleString("en-GB", {
                                                    day: "2-digit",
                                                    month: "2-digit",
                                                    year: "numeric",
                                                    hour: "2-digit",
                                                    minute: "2-digit"
                                                })}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="flex items-center justify-center min-h-[230px] text-center">
                            <p className="text-sm">No results.</p>
                        </div>
                    )}
                </div>
            )}
        </ScrollArea>
    );
};

export default OrdersTimeline;